/**
 * ScoreboardPage.jsx
 * Sidebar entry point for the public scoreboard.
 * Fetches available tournaments and shows a selector + embedded scoreboard view.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Radio, RefreshCw, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import useAuthStore from '@/stores/authStore';

export default function ScoreboardPage() {
  const { user } = useAuthStore();
  const orgSlug  = user?.memberships?.[0]?.slug || 'btcc';
  const token    = () => localStorage.getItem('btccp_access_token');
  const navigate = useNavigate();

  const [tournaments, setTournaments] = useState([]);
  const [selectedTid, setSelectedTid] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchTournaments = useCallback(async () => {
    try {
      const res = await fetch(`/api/orgs/${orgSlug}/tournaments`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTournaments(data);
        if (data.length) setSelectedTid(data[0].id);
      }
    } catch (_) {}
    finally { setLoading(false); }
  }, [orgSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchTournaments(); }, [fetchTournaments]);

  const goToScoreboard = () => {
    if (selectedTid) navigate(`/public/tournaments/${selectedTid}`);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-[rgba(239,68,68,0.1)] flex items-center justify-center">
            <Radio className="h-5 w-5 text-[#ef4444]"/>
          </div>
          <div>
            <h1 className="text-2xl font-black text-[#f0f2f7]">Live Scoreboard</h1>
            <p className="text-sm text-[#8a93a3]">Public scoreboard — real-time results</p>
          </div>
        </div>
        <button onClick={fetchTournaments}
          className="p-2 rounded-lg text-[#8a93a3] hover:text-[#f0f2f7] hover:bg-[#161a26] transition-colors">
          <RefreshCw className="h-4 w-4"/>
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-14 bg-[#1d2230] rounded-xl"/>
          <Skeleton className="h-14 bg-[#1d2230] rounded-xl"/>
        </div>
      ) : tournaments.length === 0 ? (
        <Card className="bg-[#161a26] border-[#252a37]">
          <CardContent className="p-10 text-center">
            <Radio className="h-10 w-10 text-[#8a93a3] mx-auto mb-3"/>
            <p className="text-[#f0f2f7] font-semibold">No tournaments found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4 max-w-lg">
          {/* Tournament selector */}
          <div className="space-y-2">
            <p className="text-sm text-[#8a93a3] font-semibold uppercase tracking-wider">Select Tournament</p>
            <div className="space-y-2">
              {tournaments.map(t => (
                <button key={t.id}
                  onClick={() => setSelectedTid(t.id)}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-all flex items-center justify-between gap-3 ${
                    selectedTid === t.id
                      ? 'bg-[rgba(245,197,24,0.08)] border-[rgba(245,197,24,0.4)] text-[#f5c518]'
                      : 'bg-[#10131c] border-[#252a37] text-[#f0f2f7] hover:border-[#f5c518]/40'
                  }`}>
                  <div>
                    <p className="font-semibold">{t.name}</p>
                    {t.season && <p className="text-xs text-[#8a93a3]">{t.season}</p>}
                  </div>
                  {selectedTid === t.id && (
                    <span className="text-[10px] font-bold bg-[rgba(245,197,24,0.15)] text-[#f5c518] px-2 py-0.5 rounded-full">
                      Selected
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Open scoreboard button */}
          <Button
            onClick={goToScoreboard}
            disabled={!selectedTid}
            className="w-full h-12 bg-[#ef4444] hover:bg-[#dc2626] text-white font-black text-base gap-2">
            <Radio className="h-5 w-5"/>
            Open Live Scoreboard
            <ExternalLink className="h-4 w-4 ml-1"/>
          </Button>
          <p className="text-[10px] text-[#8a93a3] text-center">
            Opens the public scoreboard — use the browser Back button to return here
          </p>
        </div>
      )}
    </div>
  );
}
